/**
 * Talks to services/api. Types come straight from the server code so the two can't drift.
 * The app and API share one address in production: the app at /, the API under /api.
 */
import type { AlertContact, AlertRoute, AlertRule, AlertRuleState, DeviceNote, Fence, NoteKind, Ticket, TicketKind, TicketMessage, TicketStatus } from "@think-control/core";
export type { AlertContact, AlertRoute, AlertRule, AlertRuleState, DeviceNote, Fence, NoteKind, Ticket, TicketKind, TicketMessage, TicketStatus };
import type { Account, AccountSettings, Alert, AuditEntry, Crop, CropRun, Entitlement, Insight, PlanInfo, PlanRequest, Report, Program, Run, ChecklistItem, Plan, Role, Site, TermsAcceptance, User } from "@think-control/core";
import type { CommandRecord, DeviceEvent, PortConfig, Reading } from "@think-control/device";
import type { ModbusDevice } from "../../../packages/core/src/modbus-devices.ts";
import type { ChartSeries } from "../../../packages/core/src/trend.ts";
import type { Check as ServerCheck, Sample as ServerSample, ServerReport } from "../../../packages/core/src/server-checks.ts";
export type { ServerCheck, ServerReport, ServerSample };
import type { DeviceRow } from "../../../services/api/src/store.ts";
import type { EuiGuess } from "../../../packages/core/src/eui.ts";
import type { ProgramShadow, ShadowSummary, Verdict as ShadowVerdict } from "../../../services/api/src/shadow-summary.ts";
import type { FindResult, FoundSubscription, PayPalPlan, PayPalSubscription } from "../../../services/api/src/paypal.ts";
import type { LegacyPrice, Payment } from "@think-control/core";
export type { ProgramShadow, ShadowSummary, ShadowVerdict };

/** One farm on the staff Shadow check page (GET /admin/shadow). */
export interface FarmShadow {
  accountId: string;
  name: string;
  programs: ShadowSummary["programs"];
  totals: ShadowSummary["totals"];
  verdicts: ShadowSummary["verdicts"];
  worst: ProgramShadow[];
}
export interface ShadowFleet {
  running: boolean;
  since: number;
  totals: { fired: number; switched: number; already: number; missed: number; waiting: number; unexpected: number };
  programs: { total: number; enabled: number; compared: number; off: number; triggered: number };
  verdicts: ShadowSummary["verdicts"];
  farms: FarmShadow[];
}

export type { ModbusDevice };
export type { Account, AccountSettings, Alert, AuditEntry, Crop, CropRun, Entitlement, Insight, PlanInfo, PlanRequest, Report, Program, Run, ChecklistItem, CommandRecord, DeviceEvent, Plan, PortConfig, Reading, Role, Site, TermsAcceptance, User };
export type PublicUser = Omit<User, "passwordHash">;

/** GET /reports/crops */
export interface CropReport {
  from: string;
  to: string;
  timezone: string;
  tariffCents?: number;
  crops: Array<{ cropId: string; name: string; areaHa?: number; water: number; kWh: number; cost?: number; hours: number; runs: number }>;
  runs: Array<CropRun & { crop: string; device: string; outputName: string; end: number; water: number; kWh: number; hours: number; averages: Array<{ port: string; label: string; average: number; unit?: string }> }>;
}
export interface AskMessage {
  role: "user" | "assistant";
  text: string;
}

/** Body of PUT /devices/:eui/ports/:kind/:channel. */
export interface PortSetup {
  enabled: boolean;
  name?: string;
  signal?: string;
  low?: number;
  high?: number;
  unit?: string;
  tankFull?: number;
  /** Reading when the tank is empty (sensors other than FMS analog inputs say both ends). */
  tankEmpty?: number;
  litresPerPulse?: number;
  ackRequired?: boolean;
  /** "" = let the app choose. */
  widget?: string;
  displayRange?: { min: number; max: number } | null;
  /** Outputs: what says the pump is running, which readings are its own (crop reports) and, if limited, the crops it can run for. */
  link?: { runningInput?: string; ports: string[]; crops?: string[] } | null;
}

/** A device safety rule: turn an output off when an input is in a state or above a value. */
export interface SafetyRule {
  slot: number;
  input: { kind: "ai" | "di"; channel: number };
  whenAbove?: number;
  whenState?: 0 | 1;
  output: { kind: "do" | "ro"; channel: number };
}

export type ProgramDraft = Omit<Program, "id" | "accountId" | "updatedAt" | "updatedBy">;
export interface ProgramView {
  program: Program;
  run: Run;
  progress: number;
}

export interface AccountStats {
  devices: number;
  heardLastHour: number;
  users: number;
  free: boolean;
}

export interface AccountDetail {
  account: Account;
  sites: Site[];
  users: PublicUser[];
  devices: Device[];
  checklist: ChecklistItem[];
  readyToGoLive: boolean;
}
export type Device = DeviceRow;

/** A MultiPro's own safety rule (V0.75+) as the board last reported it, and in words when known. */
export interface BoardRule {
  rule: number;
  enabled: boolean;
  active: boolean;
  latched: boolean;
  offUntil?: number;
  crc: number;
  hex?: string;
  at: number;
  changedBy?: string;
  reason?: string;
  changedAt?: number;
  description: string | null;
}

export interface BoardRules {
  supported: boolean;
  firmware: string | null;
  canChange: boolean;
  drives: boolean;
  rules: BoardRule[];
}
export type { EuiGuess };

/** What a MultiPro (V0.77+) reads on its own RS485 port, and the port each channel's values go to. */
export interface BoardModbusChannel {
  channel: number;
  type: string;
  slave: number;
  fc: number;
  address: number;
  bit: number;
  at: number;
  port: string;
  name: string | null;
  unit: string | null;
  enabled: boolean | null;
}
export interface BoardModbus {
  supported: boolean;
  firmware: string | null;
  drives: boolean;
  serial: { baud: number; parity: "none" | "even" | "odd"; stopBits: 1 | 2; intervalSeconds: number } | null;
  at: number | null;
  channels: BoardModbusChannel[];
}

/** How adding a device to its gateway went (the AppKey is never in it). */
export interface Provisioning {
  id: string;
  gatewayEui: string;
  type: string;
  status: "queued" | "sent" | "done" | "failed";
  steps: Array<{ label: string; ok: boolean; detail?: string }>;
  error?: string;
  createdAt: number;
  sentAt?: number;
  finishedAt?: number;
}

export interface Gateway {
  eui: string;
  name?: string;
  at?: { lat: number; lon: number };
  devices: string[];
  /** Newest message from any device behind it. */
  lastHeard?: number;
  lastDeviceAt?: number;
  /** From the gateway's keep-alive, when it sends one. */
  lastKeepaliveAt?: number;
  online?: boolean;
}

export interface ModbusPoint {
  name: string;
  /** 1 coils, 2 discrete inputs, 3 holding registers, 4 input registers. */
  fc: number;
  /** Zero-based register offset as sent on the wire. */
  address: number;
  format: string;
  unit?: string;
  transform?: string;
  /** The register as the manual prints it. */
  docRef?: string;
  writable?: boolean;
  selected?: boolean;
  note?: string;
}

export interface ModbusProposal {
  map: { device: string; maker?: string; defaultSlave?: number; serial?: string; points: ModbusPoint[]; warnings: string[] };
  problems: string[];
  model: string;
}

export interface Me {
  user: Omit<User, "passwordHash">;
  account: Account | null;
  /** What the account's plan (or trial) allows right now; null for FMS staff. */
  plan: Entitlement | null;
  termsRequired: boolean;
  terms: { id: string; version: string; title: string } | null;
}

export interface Terms {
  id: string;
  version: string;
  title: string;
  body: string;
  publishedAt: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** How a request reaches the API: fetch normally, the sample farm in demo mode. */
export type Transport = (method: string, path: string, body: unknown, token: string | null) => Promise<{ status: number; body: any }>;

const TOKEN_KEY = "tc.token";

const OFFLINE = { status: 0, body: { error: { code: "offline", message: "Can't reach Think-Control. Check your internet connection." } } };
// Online, but no answer in time: usually a connection that went stale while the app sat idle
// (the next try opens a fresh one), not a dead internet connection.
const SLOW = { status: 0, body: { error: { code: "offline", message: "Think-Control didn't answer in time. Try again in a moment." } } };
/**
 * A request on one bar of signal can hang for minutes with no error; give up after 15 s so the
 * screen says so (and useLoad backs off). Asking Claude, or reading a Modbus manual, really
 * does take longer.
 */
const TIMEOUT_MS = 15_000;
const SLOW_MS = 120_000;
const slow = (path: string) => path.startsWith("/ai/") || path.startsWith("/admin/modbus/");

const httpTransport: Transport = async (method, path, body, token) => {
  const first = await attempt(method, path, body, token);
  // A read that timed out is tried once more straight away: after the app has sat idle, the
  // browser can stall on a connection that has quietly gone stale, and the second try gets a
  // fresh one. Never for anything that changes something (a switch must not be sent twice).
  if (first === SLOW && method === "GET") return attempt(method, path, body, token);
  return first;
};

async function attempt(method: string, path: string, body: unknown, token: string | null): Promise<{ status: number; body: any }> {
  let res: Response;
  let text: string;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(slow(path) ? SLOW_MS : TIMEOUT_MS),
    });
    // The timeout covers the body too: a reply can stall halfway.
    text = await res.text();
  } catch (e) {
    // Timed out while the phone says it's online: slow, not offline. Anything else (no
    // network at all, the server refusing the connection): offline.
    const timedOut = e instanceof DOMException && (e.name === "TimeoutError" || e.name === "AbortError");
    return timedOut && navigator.onLine !== false ? SLOW : OFFLINE;
  }
  try {
    return { status: res.status, body: text ? JSON.parse(text) : {} };
  } catch {
    return { status: res.status, body: { error: { code: "bad-reply", message: "The server sent something unexpected." } } };
  }
}

let transport: Transport = httpTransport;
let token: string | null = read(TOKEN_KEY);
const signedOutListeners = new Set<() => void>();

export function setTransport(t: Transport) {
  transport = t;
}

export function hasToken() {
  return token !== null;
}

export function setToken(t: string | null) {
  // A new sign-in or a sign-out: the last person's saved farm readings (for no signal) go.
  if (t !== token) clearSnapshots();
  token = t;
  write(TOKEN_KEY, t);
}

// ---------------------------------------------------------------- last readings, for no signal

const SNAP_PREFIX = "tc.snap.";
/** Saved readings older than this aren't shown even with no signal: too old to be any use. */
const SNAP_MAX_AGE_MS = 7 * 86_400_000;

/** Keep the last good copy of something (the farm's devices and readings) on the phone. */
export function saveSnapshot(key: string, value: unknown, now = Date.now()) {
  write(SNAP_PREFIX + key, JSON.stringify({ at: now, value }));
}

/** The last good copy and when it was saved, if there is one and it isn't too old. */
export function loadSnapshot<T>(key: string, now = Date.now()): { at: number; value: T } | undefined {
  const raw = read(SNAP_PREFIX + key);
  if (!raw) return undefined;
  try {
    const s = JSON.parse(raw) as { at: number; value: T };
    return now - s.at <= SNAP_MAX_AGE_MS ? s : undefined;
  } catch {
    return undefined;
  }
}

function clearSnapshots() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i) ?? "");
    for (const k of keys) if (k.startsWith(SNAP_PREFIX)) localStorage.removeItem(k);
  } catch {
    /* storage not available: nothing was saved */
  }
}

/** Called when the session runs out, so the app can show the sign-in screen. */
export function onSignedOut(fn: () => void) {
  signedOutListeners.add(fn);
  return () => {
    signedOutListeners.delete(fn);
  };
}

export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await transport(method, path, body, token);
  if (r.status >= 200 && r.status < 300) return r.body as T;
  const e = r.body?.error ?? {};
  const err = new ApiError(r.status, e.code ?? "error", e.message ?? "Something went wrong.");
  if (r.status === 401 && token) {
    setToken(null);
    signedOutListeners.forEach((fn) => fn());
  }
  throw err;
}

const q = (params: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
};

export interface PushDevice {
  endpoint: string;
  accountId: string;
  userAgent?: string;
  createdAt: number;
  lastOkAt?: number;
}

/** What an alert rule, contact, route or fence is made of when sent (the server fills in the rest). */
/** `measure: null` takes a tank rule back to the raw reading. */
export type AlertRuleInput = Pick<AlertRule, "name" | "devEui" | "port" | "condition" | "enabled" | "severity"> & { measure?: AlertRule["measure"] | null };
export type AlertContactInput = Pick<AlertContact, "name" | "kind" | "address" | "enabled">;
export type AlertRouteInput = Omit<AlertRoute, "id" | "accountId">;
export type FenceInput = Pick<Fence, "name" | "corners" | "start" | "end">;
const withAccount = <T extends object>(body: T, accountId?: string) => ({ ...body, ...(accountId ? { accountId } : {}) });

/** Console (FMS staff and installers): services/api/src/traffic.ts and console-commands.ts. */
export interface TrafficEntry {
  id: number;
  at: number;
  dir: "up" | "down";
  devEui: string;
  accountId?: string;
  device?: string;
  model?: string;
  gatewayEui?: string;
  fPort?: number;
  fCnt?: number;
  rssi?: number;
  snr?: number;
  hex: string;
  summary: string;
  via?: "think-control" | "fms-control";
  /** The farm's name, and the gateway by the name the farm gave it. */
  account?: string;
  gateway?: string;
}
export interface UnregisteredDevice {
  devEui: string;
  lastAt: number;
  messages: number;
  gatewayEui?: string;
  rssi?: number;
  info: Record<string, string | number>;
  last: string;
  gateway?: string;
}
export interface ConsoleDevice {
  devEui: string;
  name: string;
  model: string;
  accountId: string;
  account: string;
  firmware?: string;
  info?: Record<string, string | number>;
  poweredOnAt?: number;
  lastSeenAt?: number;
  gatewayEui?: string;
}
export interface PresetParam {
  name: string;
  label: string;
  kind: "number" | "text" | "select";
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}
export interface Preset {
  id: string;
  label: string;
  help: string;
  params?: PresetParam[];
  warning?: string;
  staffOnly?: boolean;
}

export const api = {
  // Console (FMS staff and installers)
  consoleTraffic: (opts: { since?: number; devEui?: string } = {}) =>
    call<{ traffic: TrafficEntry[]; unregistered: UnregisteredDevice[]; kept: boolean }>("GET", `/console/traffic${q(opts)}`),
  consoleDevices: () => call<{ devices: ConsoleDevice[] }>("GET", "/console/devices"),
  consoleCommands: (eui: string) => call<{ presets: Preset[]; drives: boolean; commands: CommandRecord[] }>("GET", `/console/commands/${eui}`),
  consoleSend: (devEui: string, preset: string, params: Record<string, string | number>) =>
    call<{ command: CommandRecord }>("POST", "/console/send", { devEui, preset, params }),
  // Service notes on a device; farm-by-farm cut-over (FMS staff)
  deviceNotes: (eui: string) => call<{ notes: DeviceNote[]; canWrite: boolean }>("GET", `/devices/${eui}/notes`),
  addDeviceNote: (eui: string, n: { kind: NoteKind; text: string; at?: number }) => call<{ note: DeviceNote }>("POST", `/devices/${eui}/notes`, n),
  deleteDeviceNote: (eui: string, id: string) => call<{ ok: true }>("DELETE", `/devices/${eui}/notes/${id}`),
  setControl: (accountId: string, on: boolean) =>
    call<{ account: Account }>("POST", `/admin/accounts/${accountId}/control`, on ? { on, confirm: "FMS-Control is off for this farm" } : { on }),
  // Reading alerts, who's told, fences (services/api/src/server.ts, docs/api.md)
  alertRules: (accountId?: string) => call<{ rules: AlertRule[]; states: AlertRuleState[]; fences: Fence[] }>("GET", `/alert-rules${q({ accountId })}`),
  addAlertRule: (r: AlertRuleInput, accountId?: string) => call<{ rule: AlertRule }>("POST", "/alert-rules", withAccount(r, accountId)),
  updateAlertRule: (id: string, r: Partial<AlertRuleInput>, accountId?: string) => call<{ rule: AlertRule }>("PUT", `/alert-rules/${id}`, withAccount(r, accountId)),
  deleteAlertRule: (id: string, accountId?: string) => call<{ ok: true }>("DELETE", `/alert-rules/${id}${q({ accountId })}`),
  alertContacts: (accountId?: string) => call<{ contacts: AlertContact[]; routes: AlertRoute[] }>("GET", `/alert-contacts${q({ accountId })}`),
  addAlertContact: (c: AlertContactInput, accountId?: string) => call<{ contact: AlertContact }>("POST", "/alert-contacts", withAccount(c, accountId)),
  updateAlertContact: (id: string, c: Partial<AlertContactInput>, accountId?: string) => call<{ contact: AlertContact }>("PUT", `/alert-contacts/${id}`, withAccount(c, accountId)),
  deleteAlertContact: (id: string, accountId?: string) => call<{ ok: true }>("DELETE", `/alert-contacts/${id}${q({ accountId })}`),
  addAlertRoute: (r: AlertRouteInput, accountId?: string) => call<{ route: AlertRoute }>("POST", "/alert-routes", withAccount(r, accountId)),
  updateAlertRoute: (id: string, r: Partial<AlertRouteInput>, accountId?: string) => call<{ route: AlertRoute }>("PUT", `/alert-routes/${id}`, withAccount(r, accountId)),
  deleteAlertRoute: (id: string, accountId?: string) => call<{ ok: true }>("DELETE", `/alert-routes/${id}${q({ accountId })}`),
  fences: (accountId?: string) => call<{ fences: Fence[] }>("GET", `/fences${q({ accountId })}`),
  addFence: (f: FenceInput, accountId?: string) => call<{ fence: Fence }>("POST", "/fences", withAccount(f, accountId)),
  updateFence: (id: string, f: Partial<FenceInput>, accountId?: string) => call<{ fence: Fence }>("PUT", `/fences/${id}`, withAccount(f, accountId)),
  deleteFence: (id: string, accountId?: string) => call<{ ok: true }>("DELETE", `/fences/${id}${q({ accountId })}`),
  /** FMS staff: the Server page. */
  server: () => call<ServerReport>("GET", "/admin/server"),
  /** FMS staff: the shadow check for every farm, and one farm's detail. */
  shadowAll: (days: number) => call<ShadowFleet>("GET", `/admin/shadow${q({ days: String(days) })}`),
  shadow: (accountId: string, days: number) => call<{ running: boolean; since: number; summary: ShadowSummary }>("GET", `/shadow${q({ accountId, days: String(days) })}`),
  pushKey: () => call<{ publicKey: string }>("GET", "/push/key"),
  pushDevices: () => call<{ subscriptions: PushDevice[] }>("GET", "/push/subscriptions"),
  pushOn: (subscription: PushSubscriptionJSON, accountId?: string) =>
    call<{ ok: true; accountId: string }>("POST", "/push/subscriptions", { ...subscription, ...(accountId ? { accountId } : {}) }),
  pushOff: (endpoint: string) => call<{ ok: true }>("DELETE", "/push/subscriptions", { endpoint }),
  pushTest: (endpoint?: string) => call<{ sent: number; failed: number }>("POST", "/push/test", endpoint ? { endpoint } : {}),
  login: (email: string, password: string) => call<{ token: string }>("POST", "/auth/login", { email, password }),
  logout: () => call<{ ok: true }>("POST", "/auth/logout", {}),
  acceptInvite: (inviteToken: string, password: string) => call<{ token: string }>("POST", "/auth/invite/accept", { token: inviteToken, password }),
  me: () => call<Me>("GET", "/me"),
  currentTerms: () => call<Terms>("GET", "/terms/current"),
  acceptTerms: (termsVersionId: string) => call<{ ok: true }>("POST", "/terms/accept", { termsVersionId }),

  tourSeen: () => call<{ tourSeenAt: number }>("PUT", "/me/tour", {}),
  setHome: (pins: Array<{ devEui: string; port: string }>) => call<{ home: Array<{ devEui: string; port: string }> }>("PUT", "/me/home", { pins }),
  updateDevice: (eui: string, body: { name?: string; siteId?: string; model?: string }) => call<{ device: Device }>("PUT", `/devices/${eui}`, body),
  changeDevEui: (eui: string, body: { devEui: string; history: boolean; appKey?: string }) =>
    call<{ device: Device; provisioning: Provisioning | null }>("POST", `/admin/devices/${eui}/change-eui`, body),
  setGateway: (eui: string, body: { gatewayEui?: string; appKey?: string; deviceClass?: "A" | "C"; fPort?: number; add?: boolean }) =>
    call<{ device: Device; provisioning: Provisioning | null; note?: string }>("PUT", `/devices/${eui}/gateway`, body),
  guessModel: (eui: string) => call<{ guess: EuiGuess }>("GET", `/admin/devices/guess${q({ eui })}`),
  /** `appKey` for FMS staff only; others get `appKeyEnd` and leave the field blank for it. */
  gatewayDefaults: () => call<{ appKey: string | null; appKeyEnd?: string | null }>("GET", "/gateway-defaults"),
  setGatewayDefaults: (appKey: string) => call<{ appKey: string }>("PUT", "/admin/gateway-defaults", { appKey }),
  provisioning: (eui: string) => call<{ provisioning: Provisioning | null }>("GET", `/devices/${eui}/provisioning`),
  report: (from: string, to: string, accountId?: string) => call<{ report: Report; timezone: string }>("GET", `/reports${q({ from, to, accountId })}`),
  setLocation: (eui: string, location: { lat: number; lon: number } | null) => call<{ location: unknown }>("PUT", `/devices/${eui}/location`, { location }),
  gateways: (accountId?: string) => call<{ gateways: Gateway[] }>("GET", `/gateways${q({ accountId })}`),
  renameSite: (id: string, name: string, accountId?: string) => call<{ site: Site }>("PUT", `/sites/${id}`, { name, ...(accountId ? { accountId } : {}) }),
  nameGateway: (eui: string, name: string, accountId?: string) => call<{ eui: string; name?: string }>("PUT", `/gateways/${eui}`, { name, ...(accountId ? { accountId } : {}) }),
  saveMap: (body: { view?: { lat: number; lon: number; zoom: number }; gateway?: { eui: string; lat?: number; lon?: number; remove?: boolean } }, accountId?: string) =>
    call<{ map: unknown }>("PUT", "/map", { ...body, ...(accountId ? { accountId } : {}) }),
  crops: (accountId?: string) => call<{ crops: Crop[]; askCrop: boolean }>("GET", `/crops${q({ accountId })}`),
  addCrop: (c: { name: string; areaHa?: number; colour?: string }, accountId?: string) => call<{ crop: Crop }>("POST", "/crops", { ...c, ...(accountId ? { accountId } : {}) }),
  updateCrop: (id: string, c: { name?: string; areaHa?: number | null; colour?: string; archived?: boolean }, accountId?: string) =>
    call<{ crop: Crop }>("PUT", `/crops/${id}`, { ...c, ...(accountId ? { accountId } : {}) }),
  deviceCropRuns: (eui: string) => call<{ runs: Array<CropRun & { crop: string; end: number; open: boolean }> }>("GET", `/devices/${eui}/crop-runs`),
  tagCropRun: (eui: string, output: string, cropId: string) => call<{ cropRun: CropRun }>("POST", `/devices/${eui}/crop-runs`, { output, cropId }),
  endCropRun: (id: string, accountId?: string) => call<{ ok: true }>("POST", `/crop-runs/${id}/end`, accountId ? { accountId } : {}),
  cropReport: (from: string, to: string, accountId?: string) => call<CropReport>("GET", `/reports/crops${q({ from, to, accountId })}`),
  alerts: (accountId?: string) => call<{ alerts: Alert[] }>("GET", `/alerts${q({ accountId })}`),
  ackAlert: (id: string, accountId?: string) => call<{ ok: true }>("POST", `/alerts/${encodeURIComponent(id)}/ack`, accountId ? { accountId } : {}),
  insights: (accountId?: string) => call<{ insights: Insight[] }>("GET", `/insights${q({ accountId })}`),
  /** ask: the assistant is set up; farmData: it may look at this farm's readings (Ask AI on the plan). */
  aiStatus: (accountId?: string) => call<{ ask: boolean; farmData: boolean }>("GET", `/ai${q({ accountId })}`),
  ask: (messages: AskMessage[], accountId?: string, screen?: string) =>
    call<{ answer: string; used: string[]; model: string }>("POST", "/ai/ask", { messages, ...(accountId ? { accountId } : {}), ...(screen ? { screen } : {}) }),
  programs: (accountId?: string) => call<{ programs: ProgramView[]; running: boolean }>("GET", `/programs${q({ accountId })}`),
  createProgram: (p: ProgramDraft, accountId?: string) => call<{ program: Program }>("POST", "/programs", { ...p, ...(accountId ? { accountId } : {}) }),
  createRule: (p: ProgramDraft, opts: { accountId?: string; waitForChange: boolean }) =>
    call<{ program: Program }>("POST", "/programs", { ...p, waitForChange: opts.waitForChange, ...(opts.accountId ? { accountId: opts.accountId } : {}) }),
  /** Switch a program or rule on or off. Turning a rule on while it would act straight away comes back with `needsChoice`. */
  enableProgram: (id: string, enabled: boolean, waitForChange?: boolean) =>
    call<{ program?: Program; needsChoice?: boolean; trueNow?: string[] }>("POST", `/programs/${id}/enabled`, { enabled, ...(waitForChange === undefined ? {} : { waitForChange }) }),
  updateProgram: (id: string, p: ProgramDraft) => call<{ program: Program }>("PUT", `/programs/${id}`, p),
  deleteProgram: (id: string) => call<{ ok: true }>("DELETE", `/programs/${id}`),
  programAction: (id: string, action: "start" | "pause" | "resume" | "stop" | "reset") =>
    call<{ run: Run; progress: number }>("POST", `/programs/${id}/${action}`, {}),
  accountUsers: (accountId?: string) => call<{ users: PublicUser[] }>("GET", `/account/users${q({ accountId })}`),
  inviteUser: (u: { email: string; name: string; role: Role; phone?: string }, accountId?: string) =>
    accountId ? call<{ user: PublicUser }>("POST", `/admin/accounts/${accountId}/users`, u) : call<{ user: PublicUser }>("POST", "/account/users", u),
  changeUser: (
    id: string,
    changes: { role?: Role; devices?: string[] | null; sites?: string[] | null; controlHours?: { from: string; to: string } | null; accessExpiresAt?: number | null; phone?: string },
  ) => call<{ user: PublicUser }>("PATCH", `/account/users/${id}`, changes),
  removeAccess: (id: string) => call<{ user: PublicUser }>("POST", `/account/users/${id}/disable`, {}),
  restoreAccess: (id: string) => call<{ user: PublicUser }>("POST", `/account/users/${id}/enable`, {}),
  resendUserInvite: (id: string) => call<{ ok: true }>("POST", `/account/users/${id}/resend-invite`, {}),
  settings: (accountId?: string) => call<{ settings: AccountSettings }>("GET", `/account/settings${q({ accountId })}`),
  saveSettings: (s: AccountSettings, accountId?: string) => call<{ settings: AccountSettings }>("PUT", "/account/settings", { ...s, ...(accountId ? { accountId } : {}) }),
  accountAudit: (accountId?: string) => call<{ entries: AuditEntry[] }>("GET", `/account/audit${q({ accountId })}`),
  health: () => call<{ ok: boolean; listenOnly?: boolean; mqtt?: string; database?: string; email?: string }>("GET", "/health"),
  testEmail: (to?: string) => call<{ ok: true; to: string }>("POST", "/admin/email/test", to ? { to } : {}),
  /** Switch a digital or relay output: true = on, false = off, "pulse" = pulse (if the port is set up for it). */
  setOutput: (eui: string, kind: "do" | "ro", channel: number, state: boolean | "pulse", cropId?: string) =>
    call<{ command: CommandRecord }>("POST", `/devices/${eui}/commands`, { action: "output", kind, channel, state, ...(cropId ? { cropId } : {}) }),
  /** Milesight outputs by channel name; valves can open for a set number of seconds. */
  setPort: (eui: string, port: string, state: boolean, cropId?: string, seconds?: number, percent?: number) =>
    call<{ command: CommandRecord }>("POST", `/devices/${eui}/commands`, {
      action: "output",
      port,
      state,
      ...(cropId ? { cropId } : {}),
      ...(seconds ? { seconds } : {}),
      ...(percent !== undefined ? { percent } : {}),
    }),
  setAnalogOutput: (eui: string, channel: number, value: number) =>
    call<{ command: CommandRecord }>("POST", `/devices/${eui}/commands`, { action: "analog-output", channel, value }),
  resendPort: (eui: string, kind: string, channel: number) => call<{ commands: CommandRecord[] }>("POST", `/devices/${eui}/ports/${kind}/${channel}/resend`),
  savePort: (eui: string, kind: string, channel: number, port: PortSetup) =>
    call<{ port: PortConfig; commands: CommandRecord[] }>("PUT", `/devices/${eui}/ports/${kind}/${channel}`, port),
  safetyRule: (eui: string, rule: SafetyRule, dryRun = false) =>
    call<{ command: CommandRecord | { text: string; description: string }; effective?: { value: number; unit: string } }>(
      "POST",
      `/devices/${eui}/safety-rules`,
      { ...rule, ...(dryRun ? { dryRun: true } : {}) },
    ),
  clearSafetyRule: (eui: string, slot: number | "all") => call<{ command: CommandRecord }>("DELETE", `/devices/${eui}/safety-rules/${slot}`),
  safetyTimer: (eui: string, slot: number, output: { kind: "do" | "ro"; channel: number }, minutes: number) =>
    call<{ command: CommandRecord }>("POST", `/devices/${eui}/safety-timers`, { slot, output, minutes }),
  clearSafetyTimer: (eui: string, slot: number | "all") => call<{ command: CommandRecord }>("DELETE", `/devices/${eui}/safety-timers/${slot}`),
  boardRules: (eui: string) => call<BoardRules>("GET", `/devices/${eui}/board-rules`),
  // Over-the-air bench test (FMS staff)
  bench: (eui: string) => call<import("./screens/bench-test.tsx").BenchState>("GET", `/devices/${eui}/bench`),
  benchRun: (eui: string, id: string) => call<{ run: import("./screens/bench-test.tsx").BenchRunView }>("GET", `/devices/${eui}/bench/${id}`),
  startBench: (eui: string, body: { groups: string[]; cycles: number; pauseMinutes: number; benchOnly: boolean }) => call<unknown>("POST", `/devices/${eui}/bench`, body),
  stopBench: (eui: string) => call<{ stopped: boolean }>("POST", `/devices/${eui}/bench/stop`),
  refreshBoardRules: (eui: string) => call<{ command: CommandRecord }>("POST", `/devices/${eui}/board-rules/refresh`),
  changeBoardRule: (eui: string, rule: number, action: "off-for" | "off" | "on" | "reset", opts: { minutes?: number; reason?: string } = {}) =>
    call<{ command: CommandRecord }>("POST", `/devices/${eui}/board-rules/${rule}`, { action, ...opts }),
  boardModbus: (eui: string) => call<BoardModbus>("GET", `/devices/${eui}/board-modbus`),
  readBoardModbus: (eui: string, channel: number | "all") => call<{ command: CommandRecord }>("POST", `/devices/${eui}/board-modbus/read`, { channel }),
  refreshBoardModbus: (eui: string) => call<{ command: CommandRecord }>("POST", `/devices/${eui}/board-modbus/refresh`),
  boardRulesFromToolbox: (eui: string, rules: Array<{ rule: number; hex: string }>) =>
    call<{ kept: number }>("POST", `/devices/${eui}/board-rules-from-toolbox`, { rules }),

  accounts: () => call<{ accounts: Account[]; stats?: Record<string, AccountStats> }>("GET", "/admin/accounts"),
  account: (id: string) => call<AccountDetail>("GET", `/admin/accounts/${id}`),
  createAccount: (name: string, plan: Plan) => call<{ account: Account }>("POST", "/admin/accounts", { name, plan }),
  addSite: (id: string, name: string, timezone: string) => call<{ site: Site }>("POST", `/admin/accounts/${id}/sites`, { name, timezone }),
  addUser: (id: string, u: { email: string; name: string; role: Role; phone?: string }) =>
    call<{ user: PublicUser }>("POST", `/admin/accounts/${id}/users`, u),
  addDevice: (id: string, d: { devEui: string; name: string; model: string; siteId: string; gatewayEui?: string; appKey?: string }) =>
    call<{ device: Device }>("POST", `/admin/accounts/${id}/devices`, d),
  goLive: (id: string, overrideReason?: string) => call<{ account: Account; invited: number }>("POST", `/admin/accounts/${id}/go-live`, overrideReason ? { overrideReason } : {}),
  turnOff: (id: string, reason: string) => call<{ account: Account }>("POST", `/admin/accounts/${id}/turn-off`, { reason }),
  removeAccount: (id: string, confirm: string) => call<{ removed: string }>("DELETE", `/admin/accounts/${id}`, { confirm }),
  mergeAccount: (id: string, into: string, siteName?: string) =>
    call<{ account: Account; merged: { sites: number; devices: number; users: number; gateways: number; programs: number } }>("POST", `/admin/accounts/${id}/merge`, { into, ...(siteName ? { siteName } : {}) }),
  turnOn: (id: string) => call<{ account: Account }>("POST", `/admin/accounts/${id}/turn-on`, {}),
  setBilling: (id: string, b: { mode: "standard" } | { mode: "free"; reason: string; until?: number }) =>
    call<{ account: Account }>("PUT", `/admin/accounts/${id}/billing`, b),
  setPlan: (id: string, plan: Plan) => call<{ account: Account }>("PUT", `/admin/accounts/${id}/plan`, { plan }),
  setTrial: (id: string, days: number) => call<{ account: Account }>("PUT", `/admin/accounts/${id}/trial`, { days }),
  tickets: (accountId?: string) => call<{ tickets: Ticket[] }>("GET", `/tickets${q({ accountId })}`),
  allTickets: (status?: TicketStatus) => call<{ tickets: Array<Ticket & { accountName: string }>; open: number }>("GET", `/admin/tickets${q({ status })}`),
  ticket: (id: string) => call<{ ticket: Ticket; messages: TicketMessage[]; accountName: string }>("GET", `/tickets/${encodeURIComponent(id)}`),
  /** `forUserId` (FMS staff): the farm's person the ticket is for. */
  newTicket: (body: { kind: TicketKind; subject: string; body: string; devEui?: string; screen?: string; accountId?: string; forUserId?: string }) => call<{ ticket: Ticket }>("POST", "/tickets", body),
  replyTicket: (id: string, body: string, close?: boolean) => call<{ ticket: Ticket }>("POST", `/tickets/${encodeURIComponent(id)}/messages`, { body, ...(close ? { close: true } : {}) }),
  ticketStatus: (id: string, status: TicketStatus) => call<{ ticket: Ticket }>("POST", `/tickets/${encodeURIComponent(id)}/status`, { status }),
  planRequests: () => call<{ requests: Array<PlanRequest & { accountName: string; currentPlan?: Plan }> }>("GET", "/admin/plan-requests"),
  plan: (accountId?: string) =>
    call<{ entitlement: Entitlement; plans: PlanInfo[]; used: { devices: number; users: number }; free: boolean; pending: PlanRequest | null; payments: Payment[]; payOnline: boolean; legacy: LegacyPrice | null }>(
      "GET",
      `/plan${q({ accountId })}`,
    ),
  // PayPal subscriptions (services/api/src/paypal.ts, billing.ts)
  paypal: () => call<PayPalOverview>("GET", "/admin/paypal"),
  setPaypalPlans: (plans: Partial<Record<Plan, string>>) => call<{ plans: Partial<Record<Plan, string>> }>("PUT", "/admin/paypal/plans", { plans }),
  findPaypal: (days: number) =>
    call<{ days: number; subscriptions: FoundPayPal[]; regularPayers: FindResult["regularPayers"]; scanned: FindResult["scanned"] }>("GET", `/admin/paypal/find${q({ days })}`),
  paypalSubscription: (id: string) => call<{ subscription: PayPalSubscription & { source?: "profile" }; linkedTo?: { accountId: string; name: string } }>("GET", `/admin/paypal/subscriptions/${encodeURIComponent(id)}`),
  linkPaypal: (accountId: string, subscriptionId: string, label?: string) =>
    call<{ account: Account }>("POST", `/admin/accounts/${accountId}/paypal`, { subscriptionId, ...(label ? { label } : {}) }),
  setPaypalLegacy: (price: number, until: number) => call<{ legacy: LegacyPrice }>("PUT", "/admin/paypal/legacy", { price, until }),
  markPaypalLegacy: (accountId: string, subscriptionId: string, legacy: boolean) =>
    call<{ account: Account }>("PUT", `/admin/accounts/${accountId}/paypal/${encodeURIComponent(subscriptionId)}`, { legacy }),
  unlinkPaypal: (accountId: string, subscriptionId: string) => call<{ account: Account }>("DELETE", `/admin/accounts/${accountId}/paypal/${encodeURIComponent(subscriptionId)}`),
  refreshPaypal: (accountId: string) => call<{ account: Account }>("POST", `/admin/accounts/${accountId}/paypal/refresh`, {}),
  paypalSubscribe: (accountId?: string) => call<{ subscriptionId: string; approveUrl: string }>("POST", "/billing/paypal/subscribe", accountId ? { accountId } : {}),
  paypalConfirm: (subscriptionId: string, accountId?: string) =>
    call<{ payment: Payment }>("POST", "/billing/paypal/confirm", { subscriptionId, ...(accountId ? { accountId } : {}) }),
  requestPlan: (plan: Plan, note: string, accountId?: string) => call<{ request: PlanRequest }>("POST", "/plan-requests", { plan, note, ...(accountId ? { accountId } : {}) }),
  editDraftUser: (userId: string, u: { name?: string; email?: string; phone?: string; role?: Role }) => call<{ user: PublicUser }>("PUT", `/admin/users/${userId}`, u),
  removeDraftUser: (userId: string) => call<{ ok: true }>("DELETE", `/admin/users/${userId}`),
  staff: () => call<{ staff: PublicUser[] }>("GET", "/admin/staff"),
  addStaff: (u: { name: string; email: string; phone?: string }) => call<{ user: PublicUser }>("POST", "/admin/staff", u),
  resendStaffInvite: (id: string) => call<{ ok: true }>("POST", `/admin/staff/${id}/resend-invite`, {}),
  setStaffActive: (id: string, active: boolean) => call<{ user: PublicUser }>("PUT", `/admin/staff/${id}`, { active }),
  resendInvite: (userId: string) => call<{ ok: true }>("POST", `/admin/users/${userId}/resend-invite`, {}),
  audit: (id: string) => call<{ entries: AuditEntry[] }>("GET", `/admin/accounts/${id}/audit`),
  publishTerms: (version: string, title: string, body: string) => call<{ terms: Terms }>("POST", "/admin/terms", { version, title, body }),
  acceptances: (termsId: string) =>
    call<{ acceptances: Array<TermsAcceptance & { name: string; email: string; account: string }>; outstanding: Array<{ userId: string; name: string; email: string; account: string }> }>(
      "GET",
      `/admin/terms/${termsId}/acceptances`,
    ),
  sites: (accountId?: string) => call<{ sites: Site[] }>("GET", `/sites${q({ accountId })}`),
  devices: (accountId?: string) => call<{ devices: Device[] }>("GET", `/devices${q({ accountId })}`),
  archivedDevices: (accountId?: string) => call<{ devices: Device[] }>("GET", `/devices${q({ accountId, archived: 1 })}`).then((r) => ({ devices: r.devices.filter((d) => d.archivedAt) })),
  archiveDevice: (eui: string, reason?: string) => call<{ device: Device }>("POST", `/devices/${eui}/archive`, { reason }),
  restoreDevice: (eui: string) => call<{ device: Device }>("POST", `/devices/${eui}/restore`, {}),
  // Modbus (RS485) devices on a board: named by anyone who can set up devices; from a manual by FMS staff.
  saveModbusDevice: (eui: string, m: { id?: string; name: string; slave?: number; ports?: string[]; maker?: string; model?: string }) =>
    m.id
      ? call<{ modbusDevice: ModbusDevice }>("PUT", `/devices/${eui}/modbus-devices/${m.id}`, m)
      : call<{ modbusDevice: ModbusDevice }>("POST", `/devices/${eui}/modbus-devices`, m),
  removeModbusDevice: (eui: string, id: string) => call<{ ok: true }>("DELETE", `/devices/${eui}/modbus-devices/${id}`),
  modbusPropose: (fileName: string, data: string, hint?: string, devEui?: string) => call<ModbusProposal>("POST", "/admin/modbus/propose", { fileName, data, hint, ...(devEui ? { devEui } : {}) }),
  /** Move an RS485 device on an FMS board to another slave address (its readings' history goes with it). */
  moveRs485: (eui: string, slave: number, to: number) => call<{ moved: number; slave: number }>("POST", `/devices/${eui}/rs485/${slave}/move`, { to }),
  /** Take an RS485 device off an FMS board (its readings' history stays). */
  deleteRs485: (eui: string, slave: number) => call<{ removed: number }>("DELETE", `/devices/${eui}/rs485/${slave}`),
  modbusApply: (eui: string, body: { slave: number; points: ModbusPoint[]; device?: string; replace?: boolean }) =>
    call<{ ports: PortConfig[]; switchedOff: string[]; modbusDevice: ModbusDevice }>("POST", `/admin/devices/${eui}/modbus`, body),
  latest: (eui: string) => call<{ device: Device; readings: Reading[] }>("GET", `/devices/${eui}/latest`),
  /** Newest readings of every device on the account in one go (same readings as latest()); 404 from a server before GET /latest. */
  latestAll: (accountId?: string) => call<{ latest: Record<string, Reading[]> }>("GET", `/latest${q({ accountId })}`),
  /** Reports → Chart: chosen series ("eui:port") over a period (packages/core/src/trend.ts). */
  chart: (series: string[], from: number, to: number, accountId?: string) =>
    call<{ from: number; to: number; series: ChartSeries[]; historyFrom?: number }>("GET", `/chart${q({ series: series.join(","), from, to, accountId })}`),
  readings: (eui: string, port: string, from: number, to?: number, limit = 5000) =>
    call<{ readings: Reading[] }>("GET", `/devices/${eui}/readings${q({ port, from, to, limit })}`),
  events: (eui: string, limit = 50) => call<{ events: DeviceEvent[] }>("GET", `/devices/${eui}/events${q({ limit })}`),
  commands: (eui: string, limit = 20) => call<{ commands: CommandRecord[] }>("GET", `/devices/${eui}/commands${q({ limit })}`),
};

// Browser storage can be missing or blocked (private mode, some in-app browsers).
export function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
export function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* not available: the app still works, it just forgets on reload */
  }
}

export type { Payment, PayPalSubscription };
export interface PayPalOverview {
  configured: boolean;
  env: "live" | "sandbox" | null;
  plans: Array<{ plan: Plan; name: string; price: number | null; planId?: string; paypal?: PayPalPlan; problem?: string }>;
  linked: number;
  legacy: LegacyPrice | null;
  onLegacy: number;
  problems: Array<{ accountId: string; name: string; subscriptionId: string; problem: string }>;
}
export type FoundPayPal = FoundSubscription & { linkedTo?: { accountId: string; name: string }; suggested?: { accountId: string; name: string; why: string } };
